import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import { type Palette } from "@mui/material/styles";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { sorted } from "src/helpers";
import {
  LabellingCostDailyEntry,
  useGetLabellingCostDailyQuery,
} from "src/store/api/generatedApi";

const buildTenantColors = (palette: Palette["data"]): string[] => [
  palette.blue,
  palette.pink,
  palette.lightblue,
  palette.orange,
  palette.green,
  palette.red,
];

type TenantDescriptor = Readonly<{
  tenantName: string;
  tenantLongName: string;
}>;

type CombinedDailyCostChartProps = Readonly<{
  tenants: TenantDescriptor[];
  start: string;
  end: string;
  groupBy: "session_date" | "footage_date";
}>;

type TenantLoadState = Readonly<{
  entries: LabellingCostDailyEntry[];
  isLoading: boolean;
}>;

type OnTenantLoaded = (
  tenantName: string,
  entries: LabellingCostDailyEntry[],
  isLoading: boolean
) => void;

const TenantDailyDataLoader = ({
  tenantName,
  start,
  end,
  groupBy,
  onLoaded,
}: Readonly<{
  tenantName: string;
  start: string;
  end: string;
  groupBy: "session_date" | "footage_date";
  onLoaded: OnTenantLoaded;
}>) => {
  const { data, isLoading } = useGetLabellingCostDailyQuery(
    { tenantName, start, end, groupBy, costScope: "all_tenants" },
    { skip: !start || !end }
  );
  const entries = data?.entries;

  useEffect(() => {
    onLoaded(tenantName, entries ?? [], isLoading);
  }, [tenantName, entries, isLoading, onLoaded]);

  return null;
};

export const CombinedDailyCostChart = ({
  tenants,
  start,
  end,
  groupBy,
}: CombinedDailyCostChartProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { data: palette } = theme.palette;
  const totalTraceColor = theme.palette.text.primary;
  const tenantColors = useMemo(() => buildTenantColors(palette), [palette]);
  const [stateByTenant, setStateByTenant] = useState<
    Record<string, TenantLoadState>
  >({});

  const handleTenantLoaded: OnTenantLoaded = useCallback(
    (tenantName, entries, isLoading) => {
      setStateByTenant((previous) => {
        const existing = previous[tenantName];
        if (existing?.entries === entries && existing.isLoading === isLoading) {
          return previous;
        }
        return { ...previous, [tenantName]: { entries, isLoading } };
      });
    },
    []
  );

  const allResolved = tenants.every((tenant) => {
    const state = stateByTenant[tenant.tenantName];
    return state !== undefined && !state.isLoading;
  });

  const allEmpty =
    allResolved &&
    tenants.every(
      (tenant) => (stateByTenant[tenant.tenantName]?.entries ?? []).length === 0
    );

  const traces: Partial<PlotData>[] = useMemo(() => {
    const perTenantTraces = tenants.map((tenant, index) => {
      const entries = stateByTenant[tenant.tenantName]?.entries ?? [];
      const byDate = sorted([...entries], (entry) => entry.date);
      const color = tenantColors[index % tenantColors.length];
      return {
        x: byDate.map((entry) => entry.date),
        y: byDate.map((entry) => entry.total_cost_gbp),
        type: "scatter" as const,
        mode: "lines+markers" as const,
        name: tenant.tenantLongName,
        marker: { color, size: 5 },
        line: { color, width: 2 },
      };
    });

    if (tenants.length < 2) return perTenantTraces;

    const totalCostByDate = tenants
      .flatMap((tenant) => stateByTenant[tenant.tenantName]?.entries ?? [])
      .reduce<Map<string, number>>((accumulator, entry) => {
        return accumulator.set(
          entry.date,
          (accumulator.get(entry.date) ?? 0) + entry.total_cost_gbp
        );
      }, new Map());

    const sortedDates = [...totalCostByDate.keys()].sort();

    const totalTrace: Partial<PlotData> = {
      x: sortedDates,
      y: sortedDates.map((date) => totalCostByDate.get(date) ?? 0),
      type: "scatter" as const,
      mode: "lines+markers" as const,
      name: t("total"),
      marker: { color: totalTraceColor, size: 6 },
      line: { color: totalTraceColor, width: 3 },
    };

    return [totalTrace, ...perTenantTraces];
  }, [tenants, stateByTenant, tenantColors, t, totalTraceColor]);

  return (
    <>
      {tenants.map((tenant) => (
        <TenantDailyDataLoader
          key={tenant.tenantName}
          tenantName={tenant.tenantName}
          start={start}
          end={end}
          groupBy={groupBy}
          onLoaded={handleTenantLoaded}
        />
      ))}
      {!allResolved ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : allEmpty ? (
        <Alert severity="info">{t("noDataAvailable")}</Alert>
      ) : (
        <Plot
          data={traces}
          layout={{
            autosize: true,
            xaxis: { type: "date" },
            yaxis: { title: { text: `${t("cost")} (£)` } },
            margin: { l: 60, r: 30, t: 30, b: 60 },
            hovermode: "x unified",
            plot_bgcolor: "transparent",
            paper_bgcolor: "transparent",
            legend: { orientation: "h", y: -0.2 },
          }}
          config={{
            responsive: true,
            displayModeBar: false,
            displaylogo: false,
          }}
          style={{ width: "100%", height: "350px" }}
          useResizeHandler
        />
      )}
    </>
  );
};
